import React, { useEffect, useRef, useState } from 'react';
import { Calendar, CalendarCheck, Type, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Place signature and detail boxes on a document's pages.
 *
 * Positions are fractions of the page with the origin at the top-left —
 * the signing provider's own placement contract — so a box needs no
 * conversion beyond naming its party (`from` or `to:<index>`, see toProvider).
 *
 * Adapted from pages/SendEsign.js's placement screen (that page belongs to
 * the retiring in-product signer and is left untouched). Pages render via
 * pdfjs, dynamically imported so the module never loads in the jsdom suite;
 * the worker is the version-locked public/pdf.worker.min.mjs.
 */

const SIG_W = 0.3;
const SIG_H = 0.045;
const TEXT_W = 0.3;
const TEXT_H = 0.04;

let fieldSeq = 0;
const nextFieldId = () => `f${Date.now().toString(36)}${(fieldSeq += 1)}`;

/** Boxes as the provider takes them. */
export const toProvider = (fields) => fields.map((f) => {
  const box = {
    party: f.party === 'from' ? 'from' : `to:${f.recipient}`,
    kind: f.kind,
    page: f.page,
    x: Math.max(0, Math.min(f.x, 1 - f.w)),
    y: Math.max(0, Math.min(f.y, 1 - f.h)),
    w: f.w,
    h: f.h,
  };
  if ((f.kind === 'text' || f.kind === 'date') && f.value) box.value = String(f.value);
  return box;
});

const PlacementPages = ({ pdfUrl, fields, setFields, recipients, fromName, fromSigned, onToggleFromSign }) => {
  const [pages, setPages] = useState([]);
  const canvasRefs = useRef({});
  const metasRef = useRef([]);
  const wrapRef = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pdfjs = await import('pdfjs-dist');
      pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
      const docp = await pdfjs.getDocument({ url: pdfUrl }).promise;
      const containerW = Math.min(
        (wrapRef.current && wrapRef.current.clientWidth) || 680, 820);
      const metas = [];
      for (let i = 1; i <= docp.numPages; i += 1) {
        const page = await docp.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const scale = containerW / base.width;
        const vp = page.getViewport({ scale });
        metas.push({ index: i - 1, w: vp.width, h: vp.height, page, vp });
      }
      if (cancelled) return;
      metasRef.current = metas;
      setPages(metas.map(({ index, w, h }) => ({ index, w, h })));
    })().catch(() => toast.error('Could not render the document preview.'));
    return () => { cancelled = true; };
  }, [pdfUrl]);

  // Paint after the canvases are committed (a timeout loses the race with
  // React's commit and leaves blank canvases).
  useEffect(() => {
    if (!pages.length) return undefined;
    let cancelled = false;
    (async () => {
      for (const m of metasRef.current) {
        const canvas = canvasRefs.current[m.index];
        if (!canvas || cancelled) continue;
        canvas.width = m.vp.width;
        canvas.height = m.vp.height;
        try {
          await m.page.render({ canvasContext: canvas.getContext('2d'), viewport: m.vp }).promise;
        } catch { /* superseded render */ }
      }
    })();
    return () => { cancelled = true; };
  }, [pages]);

  const addField = (pageIndex, party, kind, recipient = 0) => {
    setFields((prev) => {
      const n = prev.filter((f) => f.page === pageIndex).length;
      const dims = kind === 'signature'
        ? { w: SIG_W, h: SIG_H }
        : kind === 'text'
        ? { w: TEXT_W, h: TEXT_H }
        : { w: 0.16, h: 0.04 };
      return [...prev, {
        id: nextFieldId(), party, kind, page: pageIndex,
        x: 0.08 + (n % 2) * 0.42,
        y: Math.min(0.30 + Math.floor(n / 2) * 0.08, 0.9),
        ...dims, recipient, value: '',
      }];
    });
  };
  const removeField = (id) => setFields((prev) => prev.filter((f) => f.id !== id));
  const setValue = (id, value) =>
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, value } : f)));

  const pointer = (e) => {
    const t = e.touches ? e.touches[0] : e;
    return { px: t.clientX, py: t.clientY };
  };
  const startDrag = (e, f, pageEl) => {
    if (e.target.tagName === 'INPUT' || e.target.closest('[data-nodrag]')) return;
    const rect = pageEl.getBoundingClientRect();
    const { px, py } = pointer(e);
    drag.current = {
      id: f.id, rect, mode: 'move',
      dx: px - (rect.left + f.x * rect.width),
      dy: py - (rect.top + f.y * rect.height),
    };
  };
  const startResize = (e, f, pageEl) => {
    e.stopPropagation();
    drag.current = { id: f.id, rect: pageEl.getBoundingClientRect(), mode: 'resize' };
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const { px, py } = pointer(e);
    if (e.touches) e.preventDefault();
    setFields((prev) => prev.map((f) => {
      if (f.id !== d.id) return f;
      if (d.mode === 'resize') {
        const w = Math.min(Math.max((px - d.rect.left) / d.rect.width - f.x, 0.06), 1 - f.x);
        const h = Math.min(Math.max((py - d.rect.top) / d.rect.height - f.y, 0.02), 1 - f.y);
        return { ...f, w, h };
      }
      const x = Math.min(Math.max((px - d.dx - d.rect.left) / d.rect.width, 0), 1 - f.w);
      const y = Math.min(Math.max((py - d.dy - d.rect.top) / d.rect.height, 0), 1 - f.h);
      return { ...f, x, y };
    }));
  };
  const endDrag = () => { drag.current = null; };

  const boxLabel = (f) => {
    if (f.party === 'from') {
      if (f.kind === 'date_signed') return <span className="text-[11px] text-slate-500 truncate">Date you sign (auto)</span>;
      return fromSigned
        ? <span className="font-serif italic text-blue-800 text-[15px] truncate">{fromName}</span>
        : <span className="text-[11px] text-blue-700">Your signature — click to sign</span>;
    }
    const r = recipients[f.recipient];
    const who = (r && r.name) || `Signer ${f.recipient + 1}`;
    if (f.kind === 'date_signed') return <span className="text-[11px] text-emerald-700 truncate">Date {who} signs</span>;
    if (f.kind === 'initials') return <span className="text-[11px] text-emerald-700 truncate">{who} initials</span>;
    return <span className="text-[11px] text-emerald-700 truncate">{who} signs here</span>;
  };

  const btn = 'px-2 py-1 rounded-md text-white text-[11px] font-medium';

  return (
    <div ref={wrapRef}
      onMouseMove={onMove} onMouseUp={endDrag} onMouseLeave={endDrag}
      onTouchMove={onMove} onTouchEnd={endDrag}>
      {pages.length === 0 && (
        <p className="text-[13px] text-slate-400 py-8 text-center">Rendering document…</p>
      )}
      {pages.map((p) => (
        <div key={p.index} className="mb-6">
          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <span className="text-[11px] text-slate-400 mr-1">Page {p.index + 1} — add:</span>
            <button type="button" onClick={() => addField(p.index, 'from', 'signature')}
              className={`${btn} bg-blue-600 hover:bg-blue-700`}>
              Your signature
            </button>
            {recipients.map((r, i) => (
              <React.Fragment key={i}>
                <button type="button" onClick={() => addField(p.index, 'to', 'signature', i)}
                  className={`${btn} bg-emerald-600 hover:bg-emerald-700`}>
                  {(r.name || `Signer ${i + 1}`).split(' ')[0]} signature
                </button>
                <button type="button" onClick={() => addField(p.index, 'to', 'initials', i)}
                  className={`${btn} bg-emerald-500 hover:bg-emerald-600`}>
                  {(r.name || `Signer ${i + 1}`).split(' ')[0]} initials
                </button>
                <button type="button" onClick={() => addField(p.index, 'to', 'date_signed', i)}
                  className={`${btn} bg-emerald-500 hover:bg-emerald-600`}>
                  {(r.name || `Signer ${i + 1}`).split(' ')[0]} date
                </button>
              </React.Fragment>
            ))}
            <button type="button" onClick={() => addField(p.index, 'from', 'text')}
              className={`inline-flex items-center gap-1 ${btn} bg-slate-600 hover:bg-slate-700`}>
              <Type className="h-3 w-3" /> Text
            </button>
            <button type="button" onClick={() => addField(p.index, 'from', 'date')}
              title="Pick any date"
              className={`inline-flex items-center gap-1 ${btn} bg-slate-600 hover:bg-slate-700`}>
              <Calendar className="h-3 w-3" /> Date
            </button>
            <button type="button" onClick={() => addField(p.index, 'from', 'date_signed')}
              title="Stamps the date you sign"
              className={`inline-flex items-center gap-1 ${btn} bg-slate-600 hover:bg-slate-700`}>
              <CalendarCheck className="h-3 w-3" /> Today
            </button>
          </div>
          <div className="relative inline-block border border-slate-200 rounded-md overflow-hidden select-none"
            style={{ width: p.w, maxWidth: '100%' }}>
            <canvas ref={(el) => { canvasRefs.current[p.index] = el; }} style={{ display: 'block', maxWidth: '100%' }} />
            {fields.filter((f) => f.page === p.index).map((f) => (
              <div key={f.id}
                onMouseDown={(e) => startDrag(e, f, e.currentTarget.parentElement)}
                onTouchStart={(e) => startDrag(e, f, e.currentTarget.parentElement)}
                onClick={() => { if (f.party === 'from' && f.kind === 'signature') onToggleFromSign(); }}
                className={`absolute flex items-center px-1.5 rounded-sm border cursor-move ${
                  f.party === 'from'
                    ? (f.kind === 'signature' ? 'bg-blue-50/90 border-blue-400' : 'bg-white/85 border-slate-400')
                    : 'bg-emerald-50/90 border-emerald-400'
                }`}
                style={{
                  left: `${f.x * 100}%`, top: `${f.y * 100}%`,
                  width: `${f.w * 100}%`, height: `${f.h * 100}%`,
                }}>
                {f.kind === 'text' ? (
                  <input value={f.value || ''} onChange={(e) => setValue(f.id, e.target.value)}
                    placeholder="Type details…"
                    className="w-full bg-transparent text-[12px] text-slate-900 focus:outline-none" />
                ) : f.kind === 'date' ? (
                  <input type="date" value={f.value || ''} onChange={(e) => setValue(f.id, e.target.value)}
                    className="w-full bg-transparent text-[12px] text-slate-900 focus:outline-none" />
                ) : boxLabel(f)}
                <button type="button" data-nodrag aria-label="Remove box"
                  onClick={(e) => { e.stopPropagation(); removeField(f.id); }}
                  className="absolute -top-2.5 -right-2.5 bg-white border border-slate-300 rounded-full p-0.5 text-slate-400 hover:text-rose-600">
                  <X className="h-3 w-3" />
                </button>
                {(f.kind === 'text' || f.kind === 'date') && (
                  <div data-nodrag
                    onMouseDown={(e) => startResize(e, f, e.currentTarget.parentElement.parentElement)}
                    onTouchStart={(e) => startResize(e, f, e.currentTarget.parentElement.parentElement)}
                    title="Drag to resize"
                    className="absolute -bottom-1.5 -right-1.5 w-3.5 h-3.5 bg-white border border-slate-400 rounded-sm cursor-nwse-resize"
                    style={{ backgroundImage: 'linear-gradient(135deg, transparent 50%, #94a3b8 50%)' }} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PlacementPages;
